package com.evoapps.evoshift.saves

import com.evoapps.evoshift.ui.util.UiText
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.transformLatest
import kotlinx.coroutines.selects.select

/**
 * SH-186: how long a save may go unanswered while the device is offline before it counts as kept
 * on this device. Firestore writes the change to its cache at once and only answers once the
 * server has it: offline, the answer never comes until the connection returns.
 */
const val KEPT_ON_DEVICE_AFTER_MILLIS = 3_000L

/** The end of a save waited for with [PendingSaves.runOrKeepOnDevice]. */
sealed interface SaveOutcome<out T> {
    data class Done<T>(val value: T) : SaveOutcome<T>
    data class Failed(val error: Throwable) : SaveOutcome<Nothing>

    /**
     * Offline for [KEPT_ON_DEVICE_AFTER_MILLIS] with no answer: the change is in Firestore's
     * cache on this device and is sent once the connection returns. The save goes on in
     * [PendingSaves]: it reports itself once it ends (its success message, or a failure to retry).
     */
    data object KeptOnDevice : SaveOutcome<Nothing>
}

/** [onSuccess], [onFailure] or [onKept] for the outcome; a kept save is neither (the shell says so). */
inline fun <T> SaveOutcome<T>.fold(onSuccess: (T) -> Unit, onFailure: (Throwable) -> Unit, onKept: () -> Unit = {}) {
    when (this) {
        is SaveOutcome.Done -> onSuccess(value)
        is SaveOutcome.Failed -> onFailure(error)
        SaveOutcome.KeptOnDevice -> onKept()
    }
}

/** [action] for a failed save; the outcome, for chaining. */
inline fun <T> SaveOutcome<T>.onFailure(action: (Throwable) -> Unit): SaveOutcome<T> {
    if (this is SaveOutcome.Failed) action(error)
    return this
}

/** Done or kept on this device: what the screen treats as "saved". */
val SaveOutcome<*>.isSavedOrKept: Boolean get() = this !is SaveOutcome.Failed

/**
 * SH-186: [PendingSaves.run], except that a save still unanswered after [offlineAfterMillis] of
 * the device being offline ([isOnline] false) is [SaveOutcome.KeptOnDevice]: the form closes or
 * comes back to life, the shell says "Saved on this device", and the save goes on in the app's
 * scope. A slow answer while online is waited for as before. The timeout counts from the later
 * of the save starting and the connection going; coming back online within it starts over.
 */
suspend fun <T> PendingSaves.runOrKeepOnDevice(
    key: String,
    isOnline: Flow<Boolean>,
    kind: SaveKind = SaveKind.SAVE,
    payload: Any? = null,
    successMessage: UiText? = null,
    queue: Boolean = false,
    offlineAfterMillis: Long = KEPT_ON_DEVICE_AFTER_MILLIS,
    block: suspend () -> Result<T>
): SaveOutcome<T> = awaitOrKeepOnDevice(isOnline, offlineAfterMillis) { run(key, kind, payload, successMessage, queue, block) }

/**
 * Waits for [wait] (a save in the app's scope: [PendingSaves.run] or [PendingSaves.await]) unless
 * the device has been offline for [offlineAfterMillis] first: then the waiting stops (the save
 * goes on), the shell is told ([SaveNotice.KeptOnDevice]) and the outcome is [SaveOutcome.KeptOnDevice].
 */
suspend fun <T> PendingSaves.awaitOrKeepOnDevice(
    isOnline: Flow<Boolean>,
    offlineAfterMillis: Long = KEPT_ON_DEVICE_AFTER_MILLIS,
    wait: suspend () -> Result<T>
): SaveOutcome<T> = coroutineScope {
    val save = async { wait() }
    val offline = async { isOnline.offlineFor(offlineAfterMillis) }
    select {
        save.onAwait { result ->
            offline.cancel()
            result.fold({ SaveOutcome.Done(it) }, { SaveOutcome.Failed(it) })
        }
        offline.onJoin {
            // Only the waiting is cancelled: PendingSaves runs the save to its end.
            save.cancel()
            noticeKeptOnDevice()
            SaveOutcome.KeptOnDevice
        }
    }
}

/** Suspends until the connection has been gone for [millis] without a break. */
@OptIn(ExperimentalCoroutinesApi::class)
suspend fun Flow<Boolean>.offlineFor(millis: Long) {
    transformLatest { online ->
        if (!online) {
            delay(millis)
            emit(Unit)
        }
    }.first()
}
